import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { Button } from "../components/ui/button";
import { ShoppingCart, Trash2, MapPin, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";
import { redirectToCheckout } from "../lib/checkout";
import { LEGAL_VERSION } from "../legal/texts";
import { useConfirm } from "../components/ConfirmProvider";

const fmtDT = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });

export default function CartPage() {
  const [cart, setCart] = useState({ items: [], totals: { base: 0, commission: 0, total: 0 } });
  const [cgv, setCgv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [referral, setReferral] = useState(null); // {referral_credits, redeem_service_fee_cost}
  const [redeem, setRedeem] = useState("none"); // none | service_fee | cash
  const nav = useNavigate();
  const confirm = useConfirm();

  const reload = () => api.get("/cart").then((r) => setCart(r.data));
  useEffect(() => {
    reload();
    api.get("/referral/me").then((r) => setReferral(r.data)).catch(() => setReferral(null));
  }, []);

  const removeItem = async (avail_id) => {
    await api.delete(`/cart/items/${avail_id}`);
    toast.success("Retiré du panier");
    reload();
  };

  const clearAll = async () => {
    const ok = await confirm({
      title: "Vider le panier ?",
      body: "Tous les créneaux du panier seront retirés. Vous pourrez les ré-ajouter à tout moment.",
      confirmLabel: "Vider", tone: "warn",
    });
    if (!ok) return;
    await api.delete("/cart");
    toast.success("Panier vidé");
    reload();
  };

  const checkout = async () => {
    if (!cgv) return toast.error("Vous devez accepter les CGV pour continuer");
    if (cart.items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/cart/checkout", {
        cgv_accepted: true,
        cgv_version: LEGAL_VERSION,
        origin_url: window.location.origin,
        referral_redeem: redeem === "none" ? null : redeem,
      });
      if (data.cash_only) {
        toast.success(`${(data.cash_booking_ids || []).length} réservation(s) enregistrée(s) — paiement sur place.`);
        nav("/dashboard");
        return;
      }
      if ((data.cash_booking_ids || []).length > 0) {
        toast.info(`${data.cash_booking_ids.length} créneau(x) à régler sur place — vos autres cours sont en cours de paiement.`);
      }
      if (data.discount_eur > 0) {
        toast.success(`-${data.discount_eur.toFixed(2)}€ appliqués via vos crédits parrainage`);
      }
      redirectToCheckout(data.url, () => nav("/dashboard"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const empty = cart.items.length === 0;
  const onlineItems = cart.items.filter((it) => (it.payment_method || "online") === "online");
  const cashItems = cart.items.filter((it) => it.payment_method === "cash");
  const onlineTotal = onlineItems.reduce((s, it) => s + (it.amount || 0), 0);
  const cashTotal = cashItems.reduce((s, it) => s + (it.amount || 0), 0);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="cart-page">
      <div className="flex items-center gap-3 mb-6">
        <ShoppingCart className="w-7 h-7 text-sky-700" />
        <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Mon panier</h1>
        <span className="text-sm text-slate-500">({cart.items.length} créneau{cart.items.length > 1 ? "x" : ""})</span>
      </div>

      {empty ? (
        <div className="card-soft p-12 text-center">
          <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">Votre panier est vide.</p>
          <Link to="/map" className="inline-block mt-4 text-sky-700 font-medium hover:underline">Trouver un MNS →</Link>
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-6" data-testid="cart-items">
            {cart.items.map((it) => (
              <div key={it.avail_id} className="card-soft p-4 flex items-start justify-between flex-wrap gap-3" data-testid={`cart-item-${it.avail_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-slate-900 font-semibold">
                    <Clock className="w-4 h-4 text-sky-700" />
                    {fmtDT(it.start_dt)} → {new Date(it.end_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    Coach : <strong>{it.mns.first_name} {it.mns.last_name}</strong>
                    {it.pool && <> • <MapPin className="w-3 h-3 inline" /> {it.pool.name}</>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{it.age_group === "child" ? "Enfant" : "Adulte"} • Niveau {it.level}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${(it.payment_method || "online") === "cash" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-700"}`} data-testid={`cart-method-${it.avail_id}`}>
                      {(it.payment_method || "online") === "cash" ? "Espèces sur place" : "Paiement en ligne"}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-slate-900">{it.amount.toFixed(2)}€</div>
                  <div className="text-xs text-slate-500">dont {it.commission.toFixed(2)}€ frais service</div>
                  <Button size="icon" variant="ghost" className="mt-1" onClick={() => removeItem(it.avail_id)} data-testid={`remove-${it.avail_id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="card-soft p-6">
            <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Récapitulatif de votre réservation</h3>
            <p className="text-sm text-slate-600 mb-4">
              Vous êtes sur le point de réserver un cours avec un coach indépendant.<br />
              Le contrat de prestation est conclu directement entre vous et le coach.
            </p>

            <div className="border-t pt-3 space-y-1 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Cours ({cart.items.length}) :</span>
                <span className="font-medium">{cart.totals.base.toFixed(2)}€</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Frais de service MySwimmingLessons (10%) :</span>
                <span className="font-medium">{cart.totals.commission.toFixed(2)}€</span>
              </div>
              {cashItems.length > 0 && (
                <div className="flex items-center justify-between text-amber-800 pt-1">
                  <span>dont à régler sur place ({cashItems.length}) :</span>
                  <span className="font-medium">{cashTotal.toFixed(2)}€</span>
                </div>
              )}
              {onlineItems.length > 0 && (
                <div className="flex items-center justify-between text-sky-800">
                  <span>dont à payer en ligne ({onlineItems.length}) :</span>
                  <span className="font-medium">{onlineTotal.toFixed(2)}€</span>
                </div>
              )}
              <div className="flex items-center justify-between text-base pt-2 border-t mt-2">
                <span className="font-bold">Total à payer :</span>
                <span className="font-bold text-sky-700 text-xl">{cart.totals.total.toFixed(2)}€</span>
              </div>
            </div>

            {/* Iter40: referral credits redemption */}
            {referral && onlineItems.length > 0 && Number(referral.referral_credits || 0) > 0 && (
              <div className="mt-4 card-soft p-4 bg-emerald-50 border border-emerald-200" data-testid="referral-redeem-block">
                <h4 className="font-semibold text-sm text-emerald-900 mb-2">💰 Utiliser mes crédits parrainage</h4>
                <p className="text-xs text-slate-700 mb-3">Solde disponible : <strong>{referral.referral_credits} crédits</strong></p>
                <div className="space-y-2">
                  <label className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border ${redeem === "none" ? "border-emerald-500 bg-white" : "border-transparent"}`}>
                    <input type="radio" name="redeem" value="none" checked={redeem === "none"} onChange={() => setRedeem("none")} data-testid="redeem-none" />
                    <span className="text-sm">Ne pas utiliser mes crédits</span>
                  </label>
                  <label className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border ${redeem === "service_fee" ? "border-emerald-500 bg-white" : "border-transparent"} ${referral.referral_credits < referral.redeem_service_fee_cost ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <input type="radio" name="redeem" value="service_fee" checked={redeem === "service_fee"}
                      disabled={referral.referral_credits < referral.redeem_service_fee_cost}
                      onChange={() => setRedeem("service_fee")} data-testid="redeem-svc" />
                    <span className="text-sm">
                      <strong>-{referral.redeem_service_fee_cost} crédits</strong> · Frais de service offerts (≈ {cart.totals.commission.toFixed(2)}€ d'économie)
                    </span>
                  </label>
                </div>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-4 text-xs text-amber-800 flex gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Important :</strong> Le prix du cours n'inclut pas nécessairement l'accès à la piscine ou aux infrastructures sportives.
                Les frais d'entrée éventuels sont à régler directement sur place.
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mt-2 text-xs text-slate-700" data-testid="cancel-policy-block">
              <strong>Conditions d'annulation strictes :</strong> Annulation gratuite jusqu'à 24 heures avant le cours. <strong>Toute annulation effectuée moins de 24 heures avant le début du cours, ou tout no-show déclaré par le maître-nageur, entraîne la facturation automatique de 30 % du prix du cours.</strong> Pour garantir cette politique, votre carte sera enregistrée de manière sécurisée par Stripe lors du paiement (sans débit additionnel). Elle ne sera utilisée qu'en cas de pénalité.
            </div>

            <label className="flex items-start gap-2 mt-5 p-3 bg-sky-50 border-2 border-sky-200 rounded-xl cursor-pointer">
              <input type="checkbox" checked={cgv} onChange={(e) => setCgv(e.target.checked)} className="mt-1 w-4 h-4" data-testid="cgv-checkbox" />
              <span className="text-sm text-slate-800">
                Je reconnais avoir lu et accepté les{" "}
                <Link to="/legal/cgv" target="_blank" className="text-sky-700 underline font-medium">Conditions Générales de Vente</Link>
                {" "}et d'<Link to="/legal/cgu" target="_blank" className="text-sky-700 underline font-medium">Utilisation</Link>,
                et comprends que la réservation constitue un engagement contractuel avec le coach.
              </span>
            </label>

            <div className="flex flex-wrap gap-3 mt-5">
              <Button onClick={checkout} disabled={busy || !cgv} className="rounded-full bg-sky-700 hover:bg-sky-600 flex-1 min-w-[200px]" data-testid="pay-and-book-btn">
                {busy
                  ? "Redirection..."
                  : onlineItems.length === 0
                    ? `Confirmer les réservations — ${cashTotal.toFixed(2)}€ sur place`
                    : `Payer et réserver — ${onlineTotal.toFixed(2)}€`}
              </Button>
              <Link to="/map" data-testid="continue-shopping-btn">
                <Button variant="outline" className="rounded-full">Continuer mes achats</Button>
              </Link>
              <Button onClick={clearAll} variant="ghost" className="rounded-full" data-testid="clear-cart-btn">
                Vider le panier
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
